import { useMemo, useState } from 'react';

import { ArrowUpRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import routes from '@/shared/constants/routesPath';
import { cn } from '@/shared/utils';

import Button from '@/components/ui/button';
import Loading from '@/components/ui/loading';
import useCompletedSwedishMatches from '@/hooks/home/useCompletedSwedishMatches';

import ErrorState from './swedishLeague/ErrorState';
import OngoingSection from './swedishLeague/OngoingSection';
import ScorePill from './swedishLeague/ScorePill';
import TeamColumn from './swedishLeague/TeamColumn';

const formatDateLabel = (date) => {
  if (!date) return 'Recent matches';
  const today = new Date();
  const diffInDays = Math.floor(
    (today.setHours(0, 0, 0, 0) - new Date(date).setHours(0, 0, 0, 0)) /
      (1000 * 60 * 60 * 24)
  );

  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';

  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(new Date(date));
};

const groupCompletedMatches = (matches) => {
  const buckets = new Map();

  matches.forEach((match) => {
    const date = match.completedAt ?? match.scheduledAt ?? null;
    const key = date ? new Date(date).toISOString().slice(0, 10) : 'recent';

    if (!buckets.has(key)) {
      buckets.set(key, {
        label: formatDateLabel(date),
        matches: [],
        sortValue: date ? new Date(date).getTime() : Number.NEGATIVE_INFINITY,
      });
    }

    buckets.get(key).matches.push(match);
  });

  return Array.from(buckets.values()).sort((a, b) => b.sortValue - a.sortValue);
};

const CompletedMatchRow = ({ match }) => (
  <div className="border-b border-white/10 px-4 py-1">
    <div className="flex items-center justify-between gap-1">
      <TeamColumn team={match.homeTeam} variant="home" iconSide="right" />
      <div className="flex items-center text-2xl font-black text-white">
        <ScorePill
          value={match.homeTeam.score}
          highlight={match.homeTeam.isWinner}
        />
        <span className="text-white/40">-</span>
        <ScorePill
          value={match.awayTeam.score}
          highlight={match.awayTeam.isWinner}
        />
      </div>
      <TeamColumn
        team={match.awayTeam}
        align="right"
        variant="away"
        iconSide="left"
      />
    </div>
    {/* <div className="flex items-center justify-between text-xs text-white/50">
      <span className="truncate">{match.centerName || 'Unknown center'}</span>
      <span>{formatTime(match.completedAt ?? match.scheduledAt)}</span>
    </div> */}
  </div>
);

const CompletedGroup = ({ group }) => (
  <div className="space-y-1">
    <div
      className={cn(
        'flex items-center justify-between text-[13px]',
        'font-semibold text-white/70 sticky top-0 p-2',
        'backdrop-blur-sm'
      )}
    >
      <span className="uppercase">{group.label}</span>
      <span className="text-white/40">
        {group.matches.length} match{group.matches.length === 1 ? '' : 'es'}
      </span>
    </div>
    <div>
      {group.matches.map((match) => (
        <CompletedMatchRow key={match.id} match={match} />
      ))}
    </div>
  </div>
);

const EmptyCompletedState = () => (
  <p className="px-6 py-10 text-center text-sm text-white/70">
    No completed matches in the last month.
  </p>
);

const SwedishLeague = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const {
    completedMatches,
    isLoading: isCompletedLoading,
    hasError: hasCompletedError,
    refresh: refreshCompleted,
  } = useCompletedSwedishMatches();

  const filteredCompleted = useMemo(() => {
    if (!searchTerm) return completedMatches;
    const query = searchTerm.toLowerCase();
    return completedMatches.filter((match) => {
      const haystack = [
        match.homeTeam?.name,
        match.awayTeam?.name,
        match.centerName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [completedMatches, searchTerm]);

  const completedGroups = useMemo(
    () => groupCompletedMatches(filteredCompleted),
    [filteredCompleted]
  );

  return (
    <div
      className={cn(
        'bg-black/10 p-4 rounded-3xl text-white w-full',
        'md:sticky md:top-navbar-desktop max-h-[calc(100dvh-117px)] overflow-y-hidden flex flex-col'
      )}
    >
      <div className="flex items-baseline justify-between">
        <h3 className="text-xl">Swedish League</h3>
        <Button variant="link" asChild className="gap-1">
          <Link
            to={routes.SWEDISH_LEAGUE}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs uppercase text-white/60 "
          >
            Old version
            <ArrowUpRightIcon className="w-4 h-4 inline-block" />
          </Link>
        </Button>
      </div>

      <div className="mt-6 flex flex-col gap-8">
        <OngoingSection />

        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm uppercase text-white/60">
              Completed Matches
            </div>
          </div>
          <div>
            <label className="sr-only" htmlFor="swedish-league-search">
              Search team
            </label>
            <input
              id="swedish-league-search"
              type="search"
              placeholder="Search team"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          {isCompletedLoading && (
            <div className="flex justify-center py-12">
              <Loading />
            </div>
          )}

          {!isCompletedLoading && hasCompletedError && (
            <ErrorState
              onRetry={refreshCompleted}
              message="Couldn't load completed matches"
            />
          )}

          {!isCompletedLoading && !hasCompletedError && (
            <div className="space-y-6 max-h-[calc(100vh-117px-422px)] overflow-y-auto pr-1">
              {completedGroups.length === 0 ? (
                <EmptyCompletedState />
              ) : (
                completedGroups.map((group) => (
                  <CompletedGroup key={group.label} group={group} />
                ))
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default SwedishLeague;
